// ==============================================================================
// API Client — Comunicación autenticada con el backend
// ==============================================================================
// Todas las llamadas incluyen automáticamente el JWT de TRS Auth
// almacenado en localStorage.
// ==============================================================================

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
const TOKEN_KEY = "trs_auth_token";

/**
 * Obtiene el token JWT almacenado en localStorage.
 * Retorna null si no hay token (no autenticado).
 */
function getAuthToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(TOKEN_KEY);
}

/**
 * Construye los headers de autenticación.
 * Si hay token, incluye Authorization: Bearer <token>.
 */
function getAuthHeaders(): Record<string, string> {
    const token = getAuthToken();
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
    };
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
}

interface FetchOptions extends RequestInit {
    params?: Record<string, string | number | boolean | undefined>;
}

async function apiFetch<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options;

    let url = `${API_BASE}${endpoint}`;

    if (params) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                searchParams.append(key, String(value));
            }
        });
        const qs = searchParams.toString();
        if (qs) url += `?${qs}`;
    }

    const res = await fetch(url, {
        ...fetchOptions,
        headers: {
            ...getAuthHeaders(),
            ...fetchOptions.headers,
        },
    });

    if (!res.ok) {
        // Si es 401, limpiar token y recargar para forzar login
        if (res.status === 401) {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem("trs_auth_user");
            window.location.reload();
            throw new Error("Sesión expirada. Redirigiendo a login...");
        }
        const error = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(error.detail || `HTTP ${res.status}`);
    }

    return res.json();
}

/**
 * Helper para hacer fetch autenticado sin parsear JSON (para subida de archivos, etc.)
 */
async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const token = getAuthToken();
    const headers: Record<string, string> = {};
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    return fetch(url, {
        ...options,
        headers: {
            ...headers,
            ...options.headers,
        },
    });
}

// === Documents ===

export async function getDocuments(filters?: {
    status?: string;
    doc_type?: string;
    source?: string;
    limit?: number;
    offset?: number;
}) {
    return apiFetch<any>("/api/documents/", { params: filters as any });
}

export async function getDocument(id: string) {
    return apiFetch<any>(`/api/documents/${id}`);
}

export async function getPendingDocuments(limit = 50) {
    return apiFetch<any>("/api/documents/pending", { params: { limit } });
}

export async function getDocumentStats() {
    return apiFetch<any>("/api/documents/stats");
}

export async function uploadDocument(file: File, source = "manual") {
    const token = getAuthToken();
    const headers: Record<string, string> = {};
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    const formData = new FormData();
    formData.append("file", file);

    const res = await fetch(`${API_BASE}/api/documents/upload?source=${source}`, {
        method: "POST",
        headers,
        body: formData,
    });

    if (!res.ok) {
        if (res.status === 401) {
            localStorage.removeItem(TOKEN_KEY);
            window.location.reload();
            throw new Error("Sesión expirada");
        }
        const error = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(error.detail || `HTTP ${res.status}`);
    }

    return res.json();
}

export async function reclassifyDocument(id: string, documentType: string, corrections?: Record<string, any>) {
    return apiFetch<any>(`/api/documents/${id}/reclassify`, {
        method: "POST",
        body: JSON.stringify({ document_type: documentType, corrections }),
    });
}

export async function approveDocument(id: string, correctedData?: Record<string, any>) {
    return apiFetch<any>(`/api/documents/${id}/approve`, {
        method: "POST",
        body: JSON.stringify(correctedData ? { corrected_data: correctedData } : {}),
    });
}

export async function reprocessDocument(id: string, provider?: string, model?: string) {
    const body: Record<string, string> = {};
    if (provider) body.provider = provider;
    if (model) body.model = model;
    return apiFetch<any>(`/api/documents/${id}/reprocess`, {
        method: "POST",
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
    });
}

export function getDocumentPreviewUrl(id: string): string {
    return `${API_BASE}/api/documents/${id}/preview`;
}

/**
 * Mapa de extensiones a MIME types para fallback cuando el servidor
 * no envía Content-Type correctamente (ej: detrás de proxy).
 */
const EXT_MIME_MAP: Record<string, string> = {
    ".jpg": "image/jpeg", ".jpeg": "image/jpeg", ".png": "image/png",
    ".gif": "image/gif", ".bmp": "image/bmp", ".webp": "image/webp",
    ".tiff": "image/tiff", ".tif": "image/tiff", ".svg": "image/svg+xml",
    ".pdf": "application/pdf",
};

function guessMimeFromFilename(filename: string): string {
    const ext = filename.substring(filename.lastIndexOf(".")).toLowerCase();
    return EXT_MIME_MAP[ext] || "application/octet-stream";
}

export async function fetchDocumentPreviewBlob(id: string): Promise<{ url: string; mimeType: string }> {
    const token = getAuthToken();
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const res = await fetch(`${API_BASE}/api/documents/${id}/preview`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();

    // Determinar MIME type: primero del Content-Type header (más confiable para PDFs)
    let mimeType = "";
    const contentType = res.headers.get("content-type");
    if (contentType && contentType !== "application/octet-stream") {
        mimeType = contentType.split(";")[0].trim();
    }
    // Luego del blob.type si no se obtuvo del header
    if (!mimeType && blob.type && blob.type !== "application/octet-stream") {
        mimeType = blob.type;
    }
    // Inferir de Content-Disposition filename
    if (!mimeType) {
        const cd = res.headers.get("content-disposition");
        if (cd) {
            const match = cd.match(/filename="?([^";\n]+)"?/i);
            if (match) mimeType = guessMimeFromFilename(match[1]);
        }
    }
    // Detectar por magic bytes del blob (PDF = %PDF)
    if (!mimeType || mimeType === "application/octet-stream") {
        try {
            const arr = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
            // %PDF
            if (arr[0] === 0x25 && arr[1] === 0x50 && arr[2] === 0x44 && arr[3] === 0x46) {
                mimeType = "application/pdf";
            }
        } catch { /* ignorar */ }
    }
    if (!mimeType || mimeType === "application/octet-stream") {
        mimeType = "image/jpeg"; // fallback razonable para documentos escaneados
    }

    // Crear blob con el MIME correcto si difiere
    const correctedBlob = mimeType !== blob.type
        ? new Blob([await blob.arrayBuffer()], { type: mimeType })
        : blob;

    return { url: URL.createObjectURL(correctedBlob), mimeType };
}


export async function updateDocumentData(id: string, extractedData: Record<string, any>, documentType?: string) {
    return apiFetch<any>(`/api/documents/${id}/extracted-data`, {
        method: "PATCH",
        body: JSON.stringify({ extracted_data: extractedData, document_type: documentType }),
    });
}

// === Dictionaries ===

export async function getDictionaries(dictType?: string) {
    return apiFetch<any>("/api/dictionaries/", { params: { dict_type: dictType } });
}

export async function createDictionary(data: {
    dict_type: string;
    name: string;
    cuit?: string;
    aliases?: string[];
    metadata_extra?: Record<string, any>;
    tags?: string[];
}) {
    return apiFetch<any>("/api/dictionaries/", {
        method: "POST",
        body: JSON.stringify(data),
    });
}

export async function updateDictionary(id: string, data: Record<string, any>) {
    return apiFetch<any>(`/api/dictionaries/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
    });
}

export async function deleteDictionary(id: string) {
    return apiFetch<any>(`/api/dictionaries/${id}`, { method: "DELETE" });
}

// === QA ===

export async function getQADashboard() {
    return apiFetch<any>("/api/qa/dashboard");
}

export async function getAuditLog(params?: {
    entity_type?: string;
    limit?: number;
    offset?: number;
}) {
    return apiFetch<any>("/api/qa/audit-log", { params: params as any });
}

// === Config ===

export async function getConfigLLM() {
    return apiFetch<any>("/api/config/llm");
}

export async function getConfigModels() {
    return apiFetch<any>("/api/config/llm/models");
}

export async function getConfigApiKeys() {
    return apiFetch<any>("/api/config/llm/api-keys");
}

export async function updateConfigLLM(config: Record<string, any>) {
    return apiFetch<any>("/api/config/llm", {
        method: "PUT",
        body: JSON.stringify(config),
    });
}

export async function updateConfigApiKeys(keys: Record<string, string>) {
    return apiFetch<any>("/api/config/llm/api-keys", {
        method: "PUT",
        body: JSON.stringify(keys),
    });
}

// === Telegram Config ===

export async function getTelegramConfig() {
    return apiFetch<any>("/api/config/telegram");
}

export async function updateTelegramConfig(data: any) {
    return apiFetch<any>("/api/config/telegram", {
        method: "PUT",
        body: JSON.stringify(data),
    });
}

// === Google Drive Config ===

export async function getDriveConfig() {
    return apiFetch<any>("/api/config/google-drive");
}

export async function updateDriveConfig(data: any) {
    return apiFetch<any>("/api/config/google-drive", {
        method: "PUT",
        body: JSON.stringify(data),
    });
}

// === Token Usage ===

export async function getTokenUsageSummary(days = 7) {
    return apiFetch<any>(`/api/config/token-usage/summary`, { params: { days } });
}

export async function getTokenUsageByDocument(documentId: string) {
    return apiFetch<any>(`/api/config/token-usage/document/${documentId}`);
}

export async function getModelPricing() {
    return apiFetch<any>("/api/config/token-usage/pricing");
}

// === Health ===

export async function getHealth() {
    return apiFetch<any>("/health");
}

// === Timeline ===

/** Obtiene el historial de acciones (audit log) de un documento específico. */
export async function getDocumentTimeline(documentId: string) {
    return apiFetch<any[]>(`/api/documents/${documentId}/timeline`);
}

// === Documentos agrupados (para sección Bases de datos) ===

/** Obtiene todos los documentos completados, opcionalmente filtrados por tipo. */
export async function getAllDocuments(params?: {
    document_type?: string;
    status?: string;
    limit?: number;
    offset?: number;
}) {
    return apiFetch<any>("/api/documents/", { params: params as any });
}

// Exportar helpers para uso directo
export { API_BASE, getAuthToken, getAuthHeaders, authFetch };


// ==============================================================================
// Finanzas — cash flow, costos, vencimientos, saldos y carga manual
// ==============================================================================
// Todas usan el mismo apiFetch autenticado con el JWT de TRS Auth.

export interface CashflowMes {
    mes: string;
    ingresos: number;
    egresos: number;
    internos: number;
    neto: number;
    movimientos: number;
}

export interface CashflowResponse {
    desde: string;
    hasta: string;
    serie: CashflowMes[];
    totales: { ingresos: number; egresos: number; neto: number; internos: number };
}

export interface CostosResponse {
    desde: string;
    hasta: string;
    por_categoria: { categoria: string; tipo_costo: string; total: number; cantidad: number }[];
    por_tipo_costo: Record<string, number>;
    sin_categorizar: { cantidad: number; monto: number };
}

export interface ResumenFinanciero {
    movimientos: number;
    por_direccion: Record<string, number>;
    pendiente_de_completar: { sin_empresa_asignada: number; sin_categoria_asignada: number };
    ingresos_cargados: number;
    alerta_ingresos: string | null;
    empresas: number;
    categorias: number;
}

export interface Vencimiento {
    tipo: string;
    concepto: string;
    detalle: string;
    vencimiento: string;
    /** null = importe todavía sin confirmar. No es cero: es que no se conoce. */
    importe: number | null;
    importe_a_confirmar?: boolean;
    moneda: string;
    vencido: boolean;
}

export interface CuentaSaldo {
    cuenta_id: string;
    empresa: string;
    banco: string;
    moneda: string;
    saldo: number | null;
    fecha_saldo: string | null;
    sin_cargar: boolean;
}

export async function getResumenFinanciero() {
    return apiFetch<ResumenFinanciero>("/api/finance/resumen");
}

export async function getCashflow(desde?: string, hasta?: string, empresaId?: string) {
    return apiFetch<CashflowResponse>("/api/finance/cashflow", {
        params: { desde, hasta, empresa_id: empresaId },
    });
}

export async function getCostos(desde?: string, hasta?: string, empresaId?: string) {
    return apiFetch<CostosResponse>("/api/finance/costos", {
        params: { desde, hasta, empresa_id: empresaId },
    });
}

export async function getCostosFijos() {
    return apiFetch<{
        items: Record<string, any>[];
        total_mensual_ars: number;
        total_mensual_usd: number;
        cantidad: number;
    }>("/api/finance/costos-fijos");
}

export async function getVencimientos(dias = 60) {
    return apiFetch<{ ventana_dias: number; items: Vencimiento[]; total: number; vencidos: number }>(
        "/api/finance/vencimientos",
        { params: { dias } }
    );
}

export async function getSaldos() {
    return apiFetch<{ cuentas: CuentaSaldo[]; sin_cargar: number; nota: string }>(
        "/api/finance/saldos"
    );
}

export async function getEmpresas() {
    return apiFetch<Record<string, any>[]>("/api/finance/empresas");
}

export async function getEstadoIntegraciones() {
    return apiFetch<Record<string, any>>("/api/finance/estado-integraciones");
}

export async function importarVentas(desde: string, hasta: string, soloConfirmadas = true) {
    return apiFetch<Record<string, any>>("/api/finance/importar-ventas", {
        method: "POST",
        params: { desde, hasta, solo_confirmadas: soloConfirmadas },
    });
}

export async function cargarSaldo(cuentaId: string, fecha: string, saldo: number, nota?: string) {
    return apiFetch<Record<string, any>>("/api/finance/manual/saldo", {
        method: "POST",
        body: JSON.stringify({ cuenta_id: cuentaId, fecha, saldo, nota }),
    });
}

export async function cargarMovimientoManual(data: {
    fecha: string;
    monto: number;
    direccion: string;
    concepto: string;
    moneda?: string;
    empresa_id?: string;
    categoria_id?: string;
    periodo_servicio?: string;
    nota?: string;
}) {
    return apiFetch<Record<string, any>>("/api/finance/manual/movimiento", {
        method: "POST",
        body: JSON.stringify(data),
    });
}

// ==============================================================================
// Sistema Administrativo TRS — Operación, Alertas, KPIs, Margen, Fiscal, Config
// ==============================================================================
// Todas bajo /api/finance/ops/*, /api/finance/margen, /api/finance/fiscal/*,
// /api/config/* y /api/m2m-keys. El backend se construye en paralelo — estos
// tipos/funciones codifican contra el contrato acordado; donde falta un detalle
// se eligió lo razonable, encapsulado acá para ajustar fácil.

/** Normaliza una respuesta que puede venir como lista directa o como {results: [...]}. */
function unwrapList<T>(data: unknown): T[] {
    if (Array.isArray(data)) return data as T[];
    if (data && typeof data === "object" && Array.isArray((data as any).results)) {
        return (data as any).results as T[];
    }
    return [];
}

// === Operación: salud, corridas, fuentes ===

export interface OpsUltimoSnapshot {
    fecha: string;
    empresa_key: string;
    [key: string]: unknown;
}

export interface OpsCorridaTarea {
    estado: string;
    fin: string | null;
    error?: string | null;
}

export interface OpsFuenteNoOk {
    fuente: string;
    estado: string;
    ultima_actualizacion_ok: string | null;
    detalle?: string | null;
}

export interface OpsSalud {
    ultimo_snapshot: OpsUltimoSnapshot | null;
    ultima_corrida_por_tarea: Record<string, OpsCorridaTarea>;
    fuentes_no_ok: OpsFuenteNoOk[];
    alertas_abiertas: number;
}

export async function getOpsSalud() {
    return apiFetch<OpsSalud>("/api/finance/ops/salud");
}

export interface Corrida {
    id: string;
    tarea: string;
    agente: string | null;
    inicio: string;
    fin: string | null;
    estado: "ok" | "parcial" | "error" | "timeout" | "esperando_humano" | "en_curso";
    disparador: string | null;
    metricas: Record<string, unknown> | null;
    error: string | null;
    costo_usd: number | null;
    correlation_id: string | null;
}

export async function getOpsCorridas(params?: { tarea?: string; limit?: number }) {
    const data = await apiFetch<unknown>("/api/finance/ops/corridas", { params: params as any });
    return unwrapList<Corrida>(data);
}

export interface FuenteEstado {
    fuente: string;
    estado: "ok" | "degradado" | "caido" | "sin_configurar";
    ultima_actualizacion_ok: string | null;
    ultima_intento: string | null;
    detalle: string | null;
    registros_ultimos: number | null;
}

export async function getOpsFuentes() {
    const data = await apiFetch<unknown>("/api/finance/ops/fuentes");
    return unwrapList<FuenteEstado>(data);
}

export interface BriefPreview {
    texto: string;
    caracteres: number;
    faltantes: string[];
    snapshot_fecha: string | null;
}

export async function getBriefPreview(fecha?: string) {
    return apiFetch<BriefPreview>("/api/finance/ops/brief/previsualizar", { params: { fecha } });
}

export async function enviarBrief(fecha?: string) {
    return apiFetch<Record<string, unknown>>("/api/finance/ops/brief/enviar", {
        method: "POST",
        params: { fecha },
    });
}

// === Alertas ===

export type AlertaSeveridad = "info" | "media" | "alta" | "critica";
export type AlertaEstado = "nueva" | "vista" | "en_curso" | "resuelta" | "descartada";
export type AlertaAccion = "vista" | "en_curso" | "resolver" | "descartar" | "asignar";

export interface Alerta {
    id: string;
    tipo: string;
    severidad: AlertaSeveridad;
    empresa_id: string | null;
    entidad_tipo: string | null;
    entidad_id: string | null;
    titulo: string;
    detalle: string | null;
    estado: AlertaEstado;
    asignado_a: string | null;
    creada_por: string | null;
    created_at: string;
    resuelta_at: string | null;
    resuelta_por: string | null;
    nota_resolucion: string | null;
}

export async function getOpsAlertas(params?: {
    estado?: string;
    severidad?: string;
    tipo?: string;
    empresa_id?: string;
    limit?: number;
}) {
    const data = await apiFetch<unknown>("/api/finance/ops/alertas", { params: params as any });
    return unwrapList<Alerta>(data);
}

export interface AlertasResumen {
    por_severidad: Record<string, number>;
    por_tipo: Record<string, number>;
    /** El único campo real: lo devuelve GET /api/finance/ops/alertas/resumen.
     * Antes acá figuraba también `abiertas`, que el backend NUNCA mandó: la
     * pantalla de Alertas lo leía y su tarjeta quedaba en blanco desde siempre.
     * Se sacó el campo fantasma para que TypeScript no vuelva a avalarlo. */
    total_abiertas: number;
}

export async function getOpsAlertasResumen() {
    return apiFetch<AlertasResumen>("/api/finance/ops/alertas/resumen");
}

export async function patchOpsAlerta(
    id: string,
    body: { accion: AlertaAccion; nota?: string; asignado_a?: string }
) {
    return apiFetch<Alerta>(`/api/finance/ops/alertas/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
    });
}

// === KPIs diarios ===

export interface CashflowSemana {
    semana: string;
    saldo_inicial: number | null;
    cobros_esperados: number | null;
    pagos_comprometidos: number | null;
    saldo_proyectado: number | null;
    en_rojo: boolean;
}

export interface KpiSaldoCuenta {
    cuenta_id: string;
    banco: string;
    moneda: string;
    saldo: number | null;
    fecha_saldo: string | null;
    dias_antiguedad: number | null;
}

export interface FrescuraFuente {
    estado: string;
    ultima_actualizacion_ok: string | null;
}

export interface KpiSnapshot {
    fecha: string;
    empresa_id: string | null;
    empresa_key: string;
    posicion_liquida_ars: number | null;
    posicion_liquida_usd: number | null;
    saldos_por_cuenta: KpiSaldoCuenta[];
    deuda_total: number | null;
    deuda_vencida: number | null;
    deuda_a_confirmar: number | null;
    cuotas_30d: number | null;
    fiscal_30d: number | null;
    fiscal_30d_desconocidas: number | null;
    ventas_mtd_neto: number | null;
    ventas_mtd_bruto: number | null;
    cogs_mtd: number | null;
    margen_mtd_pct: number | null;
    cobertura_cogs_pct: number | null;
    margen_contribucion_pct: number | null;
    egresos_mtd_por_tipo: Record<string, number | null>;
    cobros_dia: number | null;
    pagos_dia: number | null;
    estructura_mensual_fija: number | null;
    estructura_mensual_variable: number | null;
    punto_equilibrio: number | null;
    venta_necesaria: number | null;
    runway_meses: number | null;
    ratio_gastos_ventas: number | null;
    cashflow_13s: CashflowSemana[];
    alertas_abiertas: number | null;
    frescura: Record<string, FrescuraFuente>;
    version_calculo: number | null;
}

export async function getOpsKpisDiarios(params?: { desde?: string; hasta?: string; empresa_id?: string }) {
    const data = await apiFetch<unknown>("/api/finance/ops/kpis-diarios", { params: params as any });
    return unwrapList<KpiSnapshot>(data);
}

export async function getOpsKpiUltimo(empresaId?: string): Promise<KpiSnapshot | null> {
    // El backend devuelve {empresa_key, snapshot: {...}|null, nota?}; se tolera
    // también el snapshot directo por si el contrato cambia.
    const r = await apiFetch<Record<string, unknown> | null>("/api/finance/ops/kpis-diarios/ultimo", {
        params: { empresa_id: empresaId },
    });
    if (r && typeof r === "object" && "snapshot" in r) {
        return ((r as { snapshot?: KpiSnapshot | null }).snapshot ?? null);
    }
    return (r as unknown as KpiSnapshot) ?? null;
}

export async function recalcularKpiDia(fecha: string, empresaId: string | undefined, motivo: string) {
    return apiFetch<Record<string, unknown>>("/api/finance/ops/kpis-diarios/recalcular", {
        method: "POST",
        params: { fecha, empresa_id: empresaId },
        body: JSON.stringify({ motivo }),
    });
}

export async function getOpsCashflow13s(empresaId?: string) {
    const data = await apiFetch<unknown>("/api/finance/ops/cashflow-13s", { params: { empresa_id: empresaId } });
    if (Array.isArray(data)) return data as CashflowSemana[];
    if (data && typeof data === "object" && Array.isArray((data as any).semanas)) {
        return (data as any).semanas as CashflowSemana[];
    }
    return [];
}

// === Margen ===
// El contrato viene en tres capas por mes (estimado al vender / reposición hoy /
// real) más margen de caja. Tolerar también el shape viejo (lista plana).

export interface MargenCapa {
    costo: number | null;
    venta_costeada: number | null;
    cobertura_pct: number | null;
    margen_ars: number | null;
    margen_pct: number | null;
}

export interface MargenCaja {
    egresos_mercaderia: number | null;
    margen_caja_ars: number | null;
    margen_caja_pct: number | null;
}

export interface MargenMes {
    mes: string;
    /** presente sólo cuando se pide ?agrupar_por=canal */
    canal?: string;
    venta_neta: number | null;
    ventas?: number | null;
    estimado_venta: MargenCapa;
    reposicion_actual: MargenCapa;
    real: MargenCapa;
    caja: MargenCaja;
}

export interface MargenResponseNuevo {
    meses: MargenMes[];
    nota?: string | null;
}

/** Shape viejo, tolerado si el backend todavía no migró. */
export interface MargenMesViejo {
    mes: string;
    venta_neta: number | null;
    costo: number | null;
    margen_pct: number | null;
    cobertura_pct: number | null;
}

export async function getMargen(params?: {
    desde?: string;
    hasta?: string;
    empresa_id?: string;
    agrupar_por?: string;
}) {
    return apiFetch<MargenResponseNuevo | MargenMesViejo[]>("/api/finance/margen", { params: params as any });
}

/** true si la respuesta viene en el shape viejo (lista plana, sin capas). */
export function esMargenShapeViejo(data: MargenResponseNuevo | MargenMesViejo[]): data is MargenMesViejo[] {
    return Array.isArray(data);
}

// === Fiscal ===

export interface FiscalResumenEmpresa {
    empresa_id: string;
    empresa: string;
    total_a_pagar: number | null;
    cantidad_importe_desconocido: number;
    vencidas: number;
    urgentes: number;
    proximos_30d: number;
}

export interface FiscalConectorEstado {
    habilitado: boolean;
    configurado: boolean;
    ultimo_estado: string | null;
    ultima_corrida_ok: string | null;
    errores_consecutivos: number;
}

export interface FiscalResumen {
    empresas: FiscalResumenEmpresa[];
    frescura: Record<string, FrescuraFuente>;
    conector: FiscalConectorEstado;
}

export async function getFiscalResumen() {
    return apiFetch<FiscalResumen>("/api/finance/fiscal/resumen");
}

export interface ObligacionFiscal {
    id: string;
    empresa: string;
    tipo: string;
    impuesto_externo: string | null;
    periodo: string | null;
    formulario: string | null;
    jurisdiccion: string | null;
    fecha_vencimiento: string | null;
    dias_restantes: number | null;
    estado_presentacion: string;
    monto: number | null;
    importe_desconocido: boolean;
    vep_generado: boolean;
    vep_pagado: boolean;
    vep_importe: number | null;
    fuente: string | null;
    frescura_fuente: string | null;
}

export async function getFiscalObligaciones(params?: {
    empresa_id?: string;
    estado_presentacion?: string;
    incluir_pagadas?: boolean;
}) {
    const data = await apiFetch<unknown>("/api/finance/fiscal/obligaciones", { params: params as any });
    return unwrapList<ObligacionFiscal>(data);
}

// Espejo EXACTO de lo que devuelve `fiscal_api.py::planes`. La versión anterior
// de este tipo inventaba campos (`entidad`, `concepto`, `monto_total`,
// `saldo_pendiente`, `proxima_cuota`) que el backend nunca mandó: la tabla de
// planes salía en blanco desde siempre, sin error visible, porque TypeScript
// sólo describe la forma esperada — no la verifica en runtime (31/08/2026).
export interface CuotaPlanFiscal {
    numero: number | null;
    total_cuotas: number | null;
    fecha_vencimiento: string | null;
    importe: number | null;
    importe_desconocido?: boolean;
    estado: string | null;
}

export interface PlanFiscal {
    id: string;
    empresa_id: string | null;
    acreedor: string;
    numero_plan: string | null;
    numero_plan_externo: string | null;
    tipo_plan: string | null;
    descripcion: string | null;
    estado: string;
    fuente: string | null;
    caducidad_riesgo: boolean | null;
    cuotas_impagas: number | null;
    cuotas_pendientes: CuotaPlanFiscal[];
}

export async function getFiscalPlanes() {
    const data = await apiFetch<unknown>("/api/finance/fiscal/planes");
    return unwrapList<PlanFiscal>(data);
}

export interface NotificacionFiscal {
    id: string;
    empresa: string;
    tipo: string;
    titulo: string;
    detalle: string | null;
    fecha: string;
    requiere_accion: boolean;
    atendida: boolean;
    nota?: string | null;
}

export async function getFiscalNotificaciones(atendida = false) {
    const data = await apiFetch<unknown>("/api/finance/fiscal/notificaciones", { params: { atendida } });
    return unwrapList<NotificacionFiscal>(data);
}

export async function atenderNotificacionFiscal(id: string, nota: string) {
    return apiFetch<Record<string, unknown>>(`/api/finance/fiscal/notificaciones/${id}/atender`, {
        method: "POST",
        body: JSON.stringify({ nota }),
    });
}

export async function getFiscalEstadoConector() {
    return apiFetch<FiscalConectorEstado>("/api/finance/fiscal/estado-conector");
}

export async function syncFiscal() {
    return apiFetch<Record<string, unknown>>("/api/finance/fiscal/sync", { method: "POST" });
}

export async function probarConectorFiscal() {
    return apiFetch<Record<string, unknown>>("/api/finance/fiscal/conector/probar", { method: "POST" });
}

// === Configuración: parámetros ===

export interface Parametro {
    clave: string;
    valor: any;
    tipo: string;
    grupo: string;
    descripcion: string | null;
    default: any;
    editable_ui: boolean;
    editable_agente: boolean;
    validacion: Record<string, any> | null;
    updated_by: string | null;
    updated_at: string | null;
    version: number | null;
    en_db: boolean;
}

export interface ParametroHistorialEntry {
    valor: any;
    motivo?: string | null;
    updated_by: string | null;
    updated_at: string;
    version: number;
}

export async function getParametros(params?: { grupo?: string; q?: string }) {
    const data = await apiFetch<unknown>("/api/config/parametros", { params: params as any });
    return unwrapList<Parametro>(data);
}

export async function getParametrosGrupos() {
    return apiFetch<string[]>("/api/config/parametros/grupos");
}

export async function getParametro(clave: string) {
    return apiFetch<Parametro & { historial?: ParametroHistorialEntry[] }>(
        `/api/config/parametros/${encodeURIComponent(clave)}`
    );
}

export async function getParametroHistorial(clave: string, limit = 50) {
    const data = await apiFetch<unknown>(`/api/config/parametros/${encodeURIComponent(clave)}/historial`, {
        params: { limit },
    });
    return unwrapList<ParametroHistorialEntry>(data);
}

export async function updateParametro(clave: string, valor: any, motivo?: string) {
    return apiFetch<Parametro>(`/api/config/parametros/${encodeURIComponent(clave)}`, {
        method: "PUT",
        body: JSON.stringify({ valor, motivo }),
    });
}

export async function resetParametro(clave: string, motivo?: string) {
    return apiFetch<Parametro>(`/api/config/parametros/${encodeURIComponent(clave)}/reset`, {
        method: "POST",
        body: JSON.stringify({ motivo }),
    });
}

// === Configuración: cron ===

export interface CronUltimaCorrida {
    estado: string;
    fin: string | null;
}

export interface CronTarea {
    tarea: string;
    tarea_celery: string;
    cron_expr: string;
    habilitada: boolean;
    descripcion: string | null;
    args: Record<string, unknown> | null;
    timeout_min: number | null;
    sla_minutos: number | null;
    ultimo_disparo: string | null;
    proxima_corrida: string | null;
    ultima_corrida: CronUltimaCorrida | null;
    version: number | null;
}

export async function getCronTareas() {
    const data = await apiFetch<unknown>("/api/config/cron");
    return unwrapList<CronTarea>(data);
}

export async function updateCronTarea(
    tarea: string,
    data: { cron_expr?: string; habilitada?: boolean; descripcion?: string }
) {
    return apiFetch<CronTarea>(`/api/config/cron/${encodeURIComponent(tarea)}`, {
        method: "PUT",
        body: JSON.stringify(data),
    });
}

export async function getCronProximas(tarea: string, n = 5) {
    return apiFetch<{ proximas: string[] }>(`/api/config/cron/${encodeURIComponent(tarea)}/proximas`, {
        params: { n },
    });
}

export async function correrCronAhora(tarea: string) {
    return apiFetch<Record<string, unknown>>(`/api/config/cron/${encodeURIComponent(tarea)}/correr-ahora`, {
        method: "POST",
    });
}

export async function reloadCron() {
    return apiFetch<Record<string, unknown>>("/api/config/cron/reload", { method: "POST" });
}

// === Configuración: conectores ===

export interface Conector {
    id: string;
    nombre: string;
    tipo: string;
    habilitado: boolean;
    frecuencia_cron: string | null;
    config: Record<string, any> | null;
    credencial_ref: string | null;
    ultima_corrida_ok: string | null;
    ultima_corrida: string | null;
    ultimo_estado: string | null;
    errores_consecutivos: number;
    fuentes: string[];
    sesiones: Record<string, unknown>[];
}

export async function getConectores() {
    const data = await apiFetch<unknown>("/api/config/conectores");
    return unwrapList<Conector>(data);
}

export async function habilitarConector(id: string) {
    return apiFetch<Conector>(`/api/config/conectores/${id}/habilitar`, { method: "POST" });
}

export async function deshabilitarConector(id: string) {
    return apiFetch<Conector>(`/api/config/conectores/${id}/deshabilitar`, { method: "POST" });
}

export async function probarConector(id: string) {
    return apiFetch<Record<string, unknown>>(`/api/config/conectores/${id}/probar`, { method: "POST" });
}

export async function updateConector(id: string, data: Record<string, unknown>) {
    return apiFetch<Conector>(`/api/config/conectores/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
    });
}

// === Configuración: agentes IA ===

export interface AgenteConsumo {
    tokens_in: number;
    tokens_out: number;
    costo_usd: number;
    corridas_ok: number;
    corridas_error: number;
}

export interface AgenteConfig {
    agente: string;
    modelo_id: string;
    proveedor: string;
    habilitado: boolean;
    presupuesto_usd_dia: number | null;
    presupuesto_usd_mes: number | null;
    horario_tarea: string | null;
    nota: string | null;
    consumo: AgenteConsumo;
}

export async function getAgentesConfig() {
    const data = await apiFetch<unknown>("/api/config/agentes");
    return unwrapList<AgenteConfig>(data);
}

export async function updateAgenteConfig(
    agente: string,
    data: { modelo_id?: string; habilitado?: boolean; presupuesto_usd_dia?: number; presupuesto_usd_mes?: number }
) {
    return apiFetch<AgenteConfig>(`/api/config/agentes/${encodeURIComponent(agente)}`, {
        method: "PUT",
        body: JSON.stringify(data),
    });
}

// === Configuración: tipos de alerta ===

export interface TipoAlerta {
    codigo: string;
    descripcion: string | null;
    severidad_default: AlertaSeveridad;
    canal_default: string[];
    escalado_horas?: number | null;
    activa: boolean;
}

export async function getTiposAlerta() {
    const data = await apiFetch<unknown>("/api/config/tipos-alerta");
    return unwrapList<TipoAlerta>(data);
}

export async function updateTipoAlerta(
    codigo: string,
    data: { severidad_default?: string; canal_default?: string[]; activa?: boolean }
) {
    return apiFetch<TipoAlerta>(`/api/config/tipos-alerta/${encodeURIComponent(codigo)}`, {
        method: "PUT",
        body: JSON.stringify(data),
    });
}

// === Configuración: mi perfil (RBAC) ===

export interface MiPerfil {
    username: string;
    rol_local: string;
    permisos_efectivos: Record<string, string[]>;
}

export async function getMiPerfil() {
    return apiFetch<MiPerfil>("/api/config/rbac/mi-perfil");
}

/** true si el permiso "recurso:accion" está en los permisos efectivos del usuario. */
export function tienePermiso(perfil: MiPerfil | null, recurso: string, accion: string): boolean {
    if (!perfil) return false;
    const acciones = perfil.permisos_efectivos?.[recurso];
    return Array.isArray(acciones) && acciones.includes(accion);
}

// === API keys M2M ===

export const M2M_SCOPES = [
    "finanzas:leer",
    "cashflow:leer",
    "deuda:leer",
    "cobros:leer",
    "alertas:leer",
    "kpis:leer",
    "ingesta:escribir",
    "conectores:leer",
] as const;

export type M2MScope = (typeof M2M_SCOPES)[number];
export type M2MAccessType = "read_only" | "read_write";

export interface M2MKey {
    id: string;
    nombre: string;
    consumidor: string;
    prefijo: string;
    scopes: string[];
    access_type: M2MAccessType;
    allowed_ips: string[] | null;
    rate_limit_rpm: number | null;
    expira: string | null;
    activa: boolean;
    last_used_at: string | null;
    total_requests: number;
    created_at: string;
    revocada_at: string | null;
}

export interface M2MKeyCreada {
    id: string;
    prefijo: string;
    key: string;
    aviso: string;
}

export async function getM2MKeys() {
    const data = await apiFetch<unknown>("/api/m2m-keys");
    return unwrapList<M2MKey>(data);
}

export async function createM2MKey(data: {
    nombre: string;
    consumidor: string;
    scopes: string[];
    access_type: M2MAccessType;
    allowed_ips?: string[];
    rate_limit_rpm?: number;
    expira?: string;
}) {
    return apiFetch<M2MKeyCreada>("/api/m2m-keys", {
        method: "POST",
        body: JSON.stringify(data),
    });
}

export async function rotarM2MKey(id: string) {
    return apiFetch<{ key: string }>(`/api/m2m-keys/${id}/rotar`, { method: "POST" });
}

export async function revocarM2MKey(id: string, motivo: string) {
    return apiFetch<Record<string, unknown>>(`/api/m2m-keys/${id}/revocar`, {
        method: "POST",
        body: JSON.stringify({ motivo }),
    });
}

// ==============================================================================
// Sistema Administrativo TRS — Segunda tanda: Posición y bancos, Tarjetas,
// Conciliación, Reglas de categorización, Reportes y Agentes (tesorero/
// controller/auditor). Mismo criterio: contra el contrato acordado, con
// unwrapList() para tolerar lista directa o {results,...}.
// ==============================================================================

/** Shape que espera FetchOptions.params — evita `any` al castear objetos de filtros tipados. */
type QueryParams = Record<string, string | number | boolean | undefined>;

// === Posición y bancos ===

export interface CuentaPosicion {
    cuenta_id: string;
    empresa: string;
    banco: string;
    tipo_cuenta: string;
    moneda: string;
    numero: string;
    saldo: number | null;
    fecha_saldo: string | null;
    fuente: string | null;
    dias_antiguedad: number | null;
    activa: boolean;
}

export interface TenenciaPosicion {
    cuenta_id: string;
    instrumento: string;
    valor_total: number | null;
    moneda: string;
    fecha: string | null;
}

export interface BancosPosicion {
    cuentas: CuentaPosicion[];
    total_ars: number | null;
    total_usd: number | null;
    tenencias: TenenciaPosicion[];
    generado_en: string | null;
}

export async function getBancosPosicion() {
    return apiFetch<BancosPosicion>("/api/finance/bancos/posicion");
}

export interface ExtractoBancario {
    id: string;
    cuenta_id: string;
    banco: string;
    periodo_desde: string | null;
    periodo_hasta: string | null;
    saldo_inicial: number | null;
    saldo_final: number | null;
    filas: number | null;
    filas_conciliadas: number | null;
    cerrado_ok: boolean;
    formato_id: string | null;
    fuente: string | null;
    documento_id: string | null;
    created_at: string;
}

export async function getBancosExtractos(params?: { cuenta_id?: string; limit?: number }) {
    const data = await apiFetch<unknown>("/api/finance/bancos/extractos", { params: params as QueryParams });
    return unwrapList<ExtractoBancario>(data);
}

export interface MovimientoBancario {
    id: string;
    cuenta_id: string;
    fecha: string;
    descripcion: string;
    debito: number | null;
    credito: number | null;
    saldo: number | null;
    moneda: string;
    contraparte_nombre: string | null;
    es_interno: boolean;
    movimiento_financiero_id: string | null;
    categoria: string | null;
    estado: string | null;
    clave_dedupe: string | null;
}

export interface ListaPaginada<T> {
    results: T[];
    total: number;
    has_more: boolean;
    next_offset: number | null;
}

export async function getBancosMovimientos(params?: {
    cuenta_id?: string;
    desde?: string;
    hasta?: string;
    sin_conciliar?: boolean;
    limit?: number;
    offset?: number;
}) {
    return apiFetch<ListaPaginada<MovimientoBancario>>("/api/finance/bancos/movimientos", {
        params: params as QueryParams,
    });
}

export async function parsearDocumentoBancario(documentId: string) {
    return apiFetch<Record<string, unknown>>(`/api/finance/bancos/documentos/${documentId}/parsear`, {
        method: "POST",
    });
}

// === Tarjetas ===

export interface ResumenTarjeta {
    id: string;
    fecha_cierre: string | null;
    fecha_vencimiento: string | null;
    saldo_actual_ars: number | null;
    saldo_actual_usd: number | null;
    pago_minimo_ars: number | null;
    pagado: boolean;
}

export interface ProximoPagoTarjeta {
    fecha_vencimiento: string | null;
    importe_ars: number | null;
    importe_usd: number | null;
}

export interface CuotaFuturaTarjeta {
    periodo: string;
    importe: number | null;
}

export interface Tarjeta {
    tarjeta_id: string;
    banco: string;
    marca: string;
    ultimos4: string;
    alias: string | null;
    titular_principal: string | null;
    activa: boolean;
    empresa: string;
    ultimo_resumen: ResumenTarjeta | null;
    proximo_pago: ProximoPagoTarjeta | null;
    cuotas_futuras: CuotaFuturaTarjeta[];
}

export async function getTarjetas() {
    const data = await apiFetch<unknown>("/api/finance/bancos/tarjetas");
    return unwrapList<Tarjeta>(data);
}

export interface MovimientoTarjeta {
    id: string;
    fecha: string;
    descripcion: string;
    comercio_normalizado: string | null;
    cuota_actual: number | null;
    cuota_total: number | null;
    monto: number | null;
    moneda: string;
    tipo: string | null;
    /** "ingreso"|"egreso" — el backend lo devuelve pero no estaba tipado acá. */
    direccion: string | null;
    reconocido: boolean;
    categoria: string | null;
    movimiento_financiero_id: string | null;
}

export async function getTarjetaMovimientos(
    tarjetaId: string,
    params?: {
        resumen_id?: string; desde?: string; hasta?: string; solo_no_reconocidos?: boolean;
        limit?: number; offset?: number;
    }
) {
    const data = await apiFetch<unknown>(`/api/finance/bancos/tarjetas/${tarjetaId}/movimientos`, {
        params: params as QueryParams,
    });
    return unwrapList<MovimientoTarjeta>(data);
}

export async function patchTarjetaMovimiento(
    id: string,
    data: { reconocido?: boolean; categoria_id?: string; nota?: string }
) {
    return apiFetch<MovimientoTarjeta>(`/api/finance/bancos/tarjetas/movimientos/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
    });
}

export interface TarjetasProyeccion {
    meses: { periodo: string; importe: number | null }[];
    costo_financiero_mes: number | null;
    /** IDs de tarjeta activa sin tabla "cuotas a vencer" en su último resumen
     * (tarjetas.py::proyeccion_6_meses) — el backend las cuenta aparte en vez
     * de asumir cero. No estaba en el tipo anterior. */
    tarjetas_sin_proyeccion: string[];
}

export async function getTarjetasProyeccion() {
    return apiFetch<TarjetasProyeccion>("/api/finance/bancos/tarjetas/proyeccion");
}

// === Conciliación ===

export interface ConciliacionResumenLado {
    fecha: string | null;
    importe: number | null;
    concepto: string | null;
}

export interface PropuestaConciliacion {
    id: string;
    tipo: string;
    a_tipo: string;
    a_id: string;
    a_resumen: ConciliacionResumenLado | null;
    b_tipo: string;
    b_id: string;
    b_resumen: ConciliacionResumenLado | null;
    regla: string | null;
    confianza: number | null;
    estado: "propuesta" | "confirmada" | "rechazada" | string;
    decidido_por: string | null;
    nota: string | null;
    created_at: string;
    decided_at: string | null;
}

export async function getConciliacionPropuestas(params?: {
    estado?: string;
    tipo?: string;
    limit?: number;
    offset?: number;
}) {
    return apiFetch<ListaPaginada<PropuestaConciliacion>>("/api/finance/conciliacion/propuestas", {
        params: params as QueryParams,
    });
}

export async function confirmarConciliacion(id: string, nota?: string) {
    return apiFetch<PropuestaConciliacion>(`/api/finance/conciliacion/${id}/confirmar`, {
        method: "POST",
        body: JSON.stringify({ nota }),
    });
}

export async function rechazarConciliacion(id: string, nota?: string) {
    return apiFetch<PropuestaConciliacion>(`/api/finance/conciliacion/${id}/rechazar`, {
        method: "POST",
        body: JSON.stringify({ nota }),
    });
}

export async function deshacerConciliacion(id: string, nota?: string) {
    return apiFetch<PropuestaConciliacion>(`/api/finance/conciliacion/${id}/deshacer`, {
        method: "POST",
        body: JSON.stringify({ nota }),
    });
}

export async function correrConciliacion(params: { desde?: string; hasta?: string; dry_run: boolean }) {
    return apiFetch<Record<string, unknown>>("/api/finance/conciliacion/correr", {
        method: "POST",
        params: params as QueryParams,
    });
}

export interface ConciliacionResumenTipo {
    confirmadas: number;
    propuestas: number;
    rechazadas: number;
}

export interface ConciliacionResumen {
    por_tipo: Record<string, ConciliacionResumenTipo>;
    pct_movimientos_bancarios_conciliados: number | null;
    [key: string]: unknown;
}

export async function getConciliacionResumen(params?: { desde?: string; hasta?: string }) {
    return apiFetch<ConciliacionResumen>("/api/finance/conciliacion/resumen", { params: params as QueryParams });
}

// === Reglas de categorización ===

export interface ReglaCategorizacion {
    id: string;
    nombre: string;
    patron: string;
    campo: string;
    categoria_id: string;
    categoria_nombre: string | null;
    direccion_forzada: string | null;
    tipo_costo_forzado: string | null;
    prioridad: number;
    activa: boolean;
    origen: string;
    ejemplos: string[] | null;
    hits: number;
    ultimo_hit: string | null;
    version: number | null;
    updated_by: string | null;
    updated_at: string | null;
}

export async function getReglas(params?: { activa?: boolean; categoria_id?: string; q?: string }) {
    const data = await apiFetch<unknown>("/api/config/reglas", { params: params as QueryParams });
    return unwrapList<ReglaCategorizacion>(data);
}

export async function createRegla(data: {
    nombre: string;
    patron: string;
    campo?: string;
    categoria_id: string;
    direccion_forzada?: string;
    tipo_costo_forzado?: string;
    prioridad?: number;
    ejemplos?: string[];
    activa?: boolean;
}) {
    return apiFetch<ReglaCategorizacion>("/api/config/reglas", {
        method: "POST",
        body: JSON.stringify(data),
    });
}

export async function getRegla(id: string) {
    return apiFetch<ReglaCategorizacion>(`/api/config/reglas/${id}`);
}

export async function updateRegla(id: string, data: Record<string, unknown>) {
    return apiFetch<ReglaCategorizacion>(`/api/config/reglas/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
    });
}

export async function deleteRegla(id: string) {
    return apiFetch<Record<string, unknown>>(`/api/config/reglas/${id}`, { method: "DELETE" });
}

export interface ReglaMatch {
    movimiento_id: string;
    fecha: string;
    concepto: string;
    monto: number | null;
    categoria_actual: string | null;
}

export interface ReglaTestResult {
    matches: ReglaMatch[];
    total_revisados: number;
    total_matches: number;
}

export async function probarRegla(id: string, limite = 20) {
    return apiFetch<ReglaTestResult>(`/api/config/reglas/${id}/probar`, {
        method: "POST",
        body: JSON.stringify({ limite }),
    });
}

export async function probarReglaNueva(data: {
    patron: string;
    campo: string;
    categoria_id: string;
    limite?: number;
}) {
    return apiFetch<ReglaTestResult>("/api/config/reglas/probar-nueva", {
        method: "POST",
        body: JSON.stringify(data),
    });
}

export async function activarRegla(id: string) {
    return apiFetch<ReglaCategorizacion>(`/api/config/reglas/${id}/activar`, { method: "POST" });
}

export async function desactivarRegla(id: string) {
    return apiFetch<ReglaCategorizacion>(`/api/config/reglas/${id}/desactivar`, { method: "POST" });
}

export async function reordenarReglas(orden: string[]) {
    return apiFetch<Record<string, unknown>>("/api/config/reglas/reordenar", {
        method: "POST",
        body: JSON.stringify({ orden }),
    });
}

export async function aplicarReglas(params?: { dry_run?: boolean; solo_sin_categoria?: boolean }) {
    return apiFetch<Record<string, unknown>>("/api/config/reglas/aplicar", {
        method: "POST",
        params: params as QueryParams,
    });
}

export async function sembrarReglas() {
    return apiFetch<Record<string, unknown>>("/api/config/reglas/sembrar", { method: "POST" });
}

export interface ReglaHistorialEntry {
    version: number;
    campo?: string;
    valor_anterior?: unknown;
    valor_nuevo?: unknown;
    updated_by: string | null;
    updated_at: string;
    motivo?: string | null;
    [key: string]: unknown;
}

export async function getReglaHistorial(id: string) {
    const data = await apiFetch<unknown>(`/api/config/reglas/${id}/historial`);
    return unwrapList<ReglaHistorialEntry>(data);
}

// === Reportes ===

export interface Reporte {
    id: string;
    tipo: "semanal" | "mensual" | "rocky_hub" | string;
    periodo: string | null;
    formato: "html" | "xlsx" | "json" | string;
    generado_at: string;
    ruta: string | null;
    tamano: number | null;
}

export async function getReportes() {
    const data = await apiFetch<unknown>("/api/finance/reportes");
    return unwrapList<Reporte>(data);
}

export async function generarReporte(data: { tipo: string; periodo?: string; enviar?: boolean }) {
    return apiFetch<Record<string, unknown>>("/api/finance/reportes/generar", {
        method: "POST",
        body: JSON.stringify(data),
    });
}

/** Descarga un archivo autenticado (Authorization header) y dispara el guardado del navegador. */
export async function descargarConAuth(url: string, nombre: string): Promise<void> {
    const res = await authFetch(`${API_BASE}${url}`);
    if (!res.ok) {
        const error = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(error.detail || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = nombre;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
}

export async function getRockyHubUltimo() {
    return apiFetch<Record<string, unknown>>("/api/finance/reportes/rocky-hub/ultimo");
}

export async function publicarRockyHub() {
    return apiFetch<Record<string, unknown>>("/api/finance/reportes/rocky-hub/publicar", { method: "POST" });
}

export async function previsualizarSemanal() {
    return apiFetch<{ texto: string }>("/api/finance/reportes/semanal/previsualizar");
}

// === Agentes: Tesorero ===

export interface TesoreroResumen {
    posicion: number | null;
    total_a_pagar_7d: number | null;
    total_a_pagar_30d: number | null;
    cobertura: number | null;
    faltante: number | null;
}

export interface TesoreroItem {
    vencimiento: string;
    tipo: string;
    concepto: string;
    empresa: string | null;
    importe: number | null;
    importe_a_confirmar?: boolean;
    cuenta_sugerida: string | null;
    motivo: string | null;
    prioridad: number | null;
}

export interface TesoreroPropuesta {
    id: string;
    semana: string;
    fecha: string;
    generada_at: string;
    estado: "propuesta" | "aprobada" | "rechazada" | "vencida" | string;
    resumen: TesoreroResumen;
    items: TesoreroItem[];
    cashflow_13s: CashflowSemana[] | null;
    narrativa: string | null;
    modelo: string | null;
    costo_usd: number | null;
    decidida_por: string | null;
    decidida_at: string | null;
    nota: string | null;
}

export async function getTesoreroPropuestas(params?: { estado?: string; limit?: number }) {
    const data = await apiFetch<unknown>("/api/finance/agentes/tesorero/propuestas", { params: params as QueryParams });
    return unwrapList<TesoreroPropuesta>(data);
}

export async function getTesoreroPropuestaUltima() {
    return apiFetch<TesoreroPropuesta | null>("/api/finance/agentes/tesorero/propuestas/ultima");
}

export async function aprobarTesoreroPropuesta(id: string, nota?: string) {
    return apiFetch<TesoreroPropuesta>(`/api/finance/agentes/tesorero/propuestas/${id}/aprobar`, {
        method: "POST",
        body: JSON.stringify({ nota }),
    });
}

export async function rechazarTesoreroPropuesta(id: string, nota?: string) {
    return apiFetch<TesoreroPropuesta>(`/api/finance/agentes/tesorero/propuestas/${id}/rechazar`, {
        method: "POST",
        body: JSON.stringify({ nota }),
    });
}

export type NombreAgente = "tesorero" | "controller" | "auditor";

export async function correrAgente(agente: NombreAgente) {
    return apiFetch<Record<string, unknown>>(`/api/finance/agentes/${agente}/correr`, { method: "POST" });
}

// === Agentes: Controller ===

export interface HallazgoController {
    id: string;
    fecha: string;
    tipo: string;
    severidad: AlertaSeveridad | string;
    titulo: string;
    detalle: string | null;
    entidad_tipo: string | null;
    entidad_id: string | null;
    estado: string;
    created_at: string;
}

export async function getControllerHallazgos(params?: { desde?: string; estado?: string; limit?: number }) {
    const data = await apiFetch<unknown>("/api/finance/agentes/controller/hallazgos", { params: params as QueryParams });
    return unwrapList<HallazgoController>(data);
}

export async function patchControllerHallazgo(id: string, estado: string) {
    return apiFetch<HallazgoController>(`/api/finance/agentes/controller/hallazgos/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ estado }),
    });
}

// === Agentes: Auditor ===

export interface InformeAuditor {
    id: string;
    periodo: string;
    generado_at: string;
    muestra_tamano: number | null;
    errores_detectados: number | null;
    tasa_error_pct: number | null;
    reglas_propuestas: number | null;
    ruta_markdown: string | null;
}

export async function getAuditorInformes(limit = 20) {
    const data = await apiFetch<unknown>("/api/finance/agentes/auditor/informes", { params: { limit } });
    return unwrapList<InformeAuditor>(data);
}

export async function getAuditorInforme(id: string) {
    return apiFetch<InformeAuditor & { markdown: string }>(`/api/finance/agentes/auditor/informes/${id}`);
}

// === Agentes: consumo de IA ===

export interface AgenteConsumoResumen {
    agente: string;
    corridas: number;
    tokens_in: number;
    tokens_out: number;
    costo_usd: number;
}

export async function getAgentesConsumo(dias = 30) {
    const data = await apiFetch<unknown>("/api/finance/agentes/consumo", { params: { dias } });
    const conPorAgente = data as { por_agente?: unknown } | null;
    if (conPorAgente && typeof conPorAgente === "object" && Array.isArray(conPorAgente.por_agente)) {
        return conPorAgente.por_agente as AgenteConsumoResumen[];
    }
    return unwrapList<AgenteConsumoResumen>(data);
}

// ==============================================================================
// Sistema Administrativo TRS — Tercera tanda: HOY, Deuda, Tarjetas: el motor,
// Confianza (P0/P2/P3/P7 de docs/diseno-sistema-administrativo/16_TABLERO_DEL_DUENO.md).
// Contratos verificados leyendo el código real de apps/backend/app/finance/
// trazabilidad.py, deuda.py y app/api/finance_ops_api.py — no son el shape
// aspiracional del documento de diseño, sino lo que el backend devuelve HOY.
// ==============================================================================

// === Trazabilidad (punto cero, teórico vs real, modo declarado/afirmativo) ===

export interface TrazabilidadHallazgo {
    tipo: "hueco_de_extracto" | "no_cierra_interno" | "moneda_sospechosa" | "sin_punto_cero" | string;
    mensaje: string;
    severidad: "info" | "media" | "alta" | string;
    desde: string | null;
    hasta: string | null;
    monto: number | null;
    periodo: string | null;
    extracto_id: string | null;
}

export type EstadoTrazabilidad = "verde" | "amarillo" | "rojo";

export interface TrazabilidadCuentaEstado {
    cuenta_id: string;
    cuenta: string | null;
    empresa_id: string | null;
    moneda: string;
    fecha: string;
    punto_cero: string | null;
    punto_cero_origen: string | null;
    punto_cero_motivo: string | null;
    saldo_ancla: number | null;
    saldo_teorico: number | null;
    saldo_real: number | null;
    saldo_real_fuente: string | null;
    saldo_real_fecha: string | null;
    brecha: number | null;
    brecha_pct: number | null;
    brecha_semaforo: EstadoTrazabilidad;
    volumen_periodo: number | null;
    volumen_total: number | null;
    volumen_trazado: number | null;
    cobertura_volumen_pct: number | null;
    cobertura_calendario_pct: number | null;
    extractos_total: number;
    extractos_cierran: number;
    extractos_cierran_pct: number | null;
    pares_total: number;
    pares_encadenan: number;
    brecha_encadenamiento: number | null;
    meses_faltantes: string[];
    frescura_dias: number | null;
    movimientos: number;
    itc: number;
    estado: EstadoTrazabilidad;
    puede_afirmar: boolean;
    hallazgos: TrazabilidadHallazgo[];
    explicacion: string | null;
    error?: string;
}

export interface TrazabilidadConsolidado {
    modo: "afirmativo" | "declarado";
    puede_afirmar: boolean;
    cobertura_pct: number;
    cobertura_por_moneda: Record<string, number>;
    moneda_mas_atrasada: string | null;
    umbral_pct: number;
    volumen_por_moneda: Record<string, { total: number; trazado: number; sin_trazar: number }>;
    flujos: number;
    flujos_verde: number;
    flujos_amarillo: number;
    flujos_rojo: number;
    flujos_sin_punto_cero: number;
    huecos_recientes: number;
    meses_faltantes_total: number;
    alertas_desvio_suspendidas: { cuenta_id: string | null; moneda: string | null; cuenta: string | null }[];
    que_falta_para_afirmar: string[];
    mensaje: string;
    prioridades: {
        cuenta_id: string | null; cuenta: string | null; moneda: string | null;
        volumen_sin_trazar: number; estado: string | null;
    }[];
    cuentas_activas_sin_datos: number;
}

export interface TrazabilidadGrupo {
    fecha: string;
    consolidado: TrazabilidadConsolidado;
    cuentas: TrazabilidadCuentaEstado[];
    config: {
        punto_cero_minimo: string | null;
        umbral_cobertura_pct: number;
        tolerancia_dias_encadenamiento: number;
        huecos_ventana_dias: number;
    };
}

export async function getTrazabilidad(params?: { fecha?: string; solo_problemas?: boolean }) {
    return apiFetch<TrazabilidadGrupo>("/api/finance/ops/trazabilidad", { params: params as QueryParams });
}

// === Deuda consolidada (la única definición de "cuánto debo") ===

export interface DeudaComponenteMonto { ars: number; usd: number; partidas: number; }
export interface DeudaTramoMonto { ars: number; usd: number; }
export interface DeudaHueco { componente: string; motivo: string; impacto: string; }
export interface DeudaAConfirmar {
    componente: string; detalle: string | null; moneda: string; vencimiento: string | null;
}
export interface DeudaExigibleVentana {
    ventana_dias: number; hasta: string; ars: number; usd: number;
    incluye_vencido: boolean; sin_fecha_ars: number;
}

/** Los 6 componentes de 16 §C1, en el orden en que se muestran. */
export const DEUDA_COMPONENTES = [
    "planes_fiscales", "fiscal_corriente", "tarjeta_exigible",
    "tarjeta_cuotas", "proveedores", "moratorias",
] as const;
export type DeudaComponenteId = (typeof DEUDA_COMPONENTES)[number];

export const DEUDA_COMPONENTE_LABEL: Record<DeudaComponenteId, string> = {
    planes_fiscales: "Planes fiscales (AFIP/ARCA/AGIP/AySA)",
    fiscal_corriente: "Fiscal corriente (IVA, IIBB, Ganancias)",
    tarjeta_exigible: "Tarjeta exigible (último resumen impago)",
    tarjeta_cuotas: "Tarjeta comprometida a futuro",
    proveedores: "Proveedores",
    moratorias: "Moratorias",
};

export interface DeudaConsolidada {
    fecha: string;
    total_ars: number;
    total_usd: number;
    vencido_ars: number;
    vencido_usd: number;
    vencido_pct: number | null;
    por_componente: Record<string, DeudaComponenteMonto>;
    por_vencimiento: Record<string, DeudaTramoMonto>;
    partidas_total: number;
    sin_fecha_ars: number;
    a_confirmar: DeudaAConfirmar[];
    a_confirmar_cantidad: number;
    huecos: DeudaHueco[];
    huecos_cantidad: number;
    completo: boolean;
    exigible_7d: DeudaExigibleVentana;
    exigible_30d: DeudaExigibleVentana;
    exigible_90d: DeudaExigibleVentana;
    empresa_id: string | null;
    nota: string;
}

export async function getDeudaConsolidada(params?: { empresa_id?: string; fecha?: string }) {
    return apiFetch<DeudaConsolidada>("/api/finance/ops/deuda-consolidada", { params: params as QueryParams });
}

// === Necesidad ("¿me alcanza?" y venta necesaria) ===

export interface NecesidadCobranzas {
    cobranzas_proyectadas: number | null;
    banda_p25?: number;
    banda_p75?: number;
    mediana_diaria?: number;
    ventana_dias?: number;
    dias?: number;
    motivo: string | null;
}

export interface Necesidad {
    fecha: string;
    empresa_id: string | null;
    venta_necesaria_estructura: number | null;
    venta_necesaria_total: number | null;
    margen_contribucion_pct: number | null;
    costo_fijo_mensual: number | null;
    exigible_del_periodo?: number;
    venta_motivo: string | null;
    exigible_hasta_fin_de_mes: number;
    exigible_30d_ars: number;
    costo_fijo_banda: { p25: number; p75: number };
    cobranzas: NecesidadCobranzas;
    brecha_caja_30d: number | null;
    alcanza: boolean | null;
    colchon_pct: number | null;
    posicion_ars?: number;
    cobranzas_proyectadas_30d?: number;
    brecha_motivo: string | null;
    huecos: DeudaHueco[];
    huecos_cantidad: number;
    supuestos: string[];
    snapshot_usado: { fecha: string | null; posicion_al: string | null; nota: string | null };
}

export async function getNecesidad(params?: { empresa_id?: string; fecha?: string }) {
    return apiFetch<Necesidad>("/api/finance/ops/necesidad", { params: params as QueryParams });
}

// === Planes fiscales, contrato REAL de /api/finance/fiscal/planes ===
// OJO: `getFiscalPlanes`/`PlanFiscal` (arriba) están tipados contra un shape
// que el backend nunca devolvió ({results:[...]} con entidad/monto_total/
// saldo_pendiente) — el endpoint real devuelve {items:[...], total} con los
// campos de abajo (verificado leyendo apps/backend/app/api/fiscal_api.py
// líneas 212-253). `unwrapList` no encuentra `results` ahí, así que
// `getFiscalPlanes()` devuelve `[]` siempre. Se deja el existente intacto
// (lo usa /finanzas/fiscal, fuera de este alcance) y se agrega éste, correcto,
// para P2.4.

export interface CuotaPlanFiscalReal {
    numero: number | null;
    total_cuotas: number | null;
    fecha_vencimiento: string;
    importe: number | null;
    importe_desconocido: boolean;
    estado: string;
}

export interface PlanFiscalReal {
    id: string;
    empresa_id: string | null;
    acreedor: string;
    numero_plan: string | null;
    numero_plan_externo: string | null;
    tipo_plan: string | null;
    descripcion: string | null;
    estado: string;
    fuente: string | null;
    caducidad_riesgo: boolean | null;
    cuotas_impagas: number | null;
    cuotas_pendientes: CuotaPlanFiscalReal[];
}

export async function getPlanesFiscalesReal(empresaId?: string) {
    const data = await apiFetch<{ items: PlanFiscalReal[]; total: number }>("/api/finance/fiscal/planes", {
        params: { empresa_id: empresaId },
    });
    return data.items ?? [];
}

// === Pasivo y consumo de tarjeta — agregación en el cliente ===
// `app/finance/pasivo_tarjeta.py::pasivo_de_tarjetas/costo_de_la_linea` existen
// en el backend (calculan línea disponible y costo de la línea) pero ningún
// router los expone todavía (grep sobre apps/backend/app/api: cero resultados
// fuera de deuda.py, que los usa internamente). No se puede armar P3.2 con
// datos reales sin ese endpoint — se declara como hueco en la pantalla.

